// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';

import { useCanEdit } from '../../../app/CaseRoleContext';
import type { GeoWarning } from '../../../bygning/geometri';
import {
  MODIFY_GROUPS,
  TOOL_GROUPS,
  commandDef,
  commandTitle,
  toolHint,
  type GeometriCommand,
  type GeometriTool,
} from '../../../bygning/geometriKeys';
import { lengthInput, parseLength } from '../../../bygning/geometriEdit';
import { COMMAND_LABEL, type ModifySession } from '../../../bygning/modifyTool';
import type { XY } from '../../../bygning/planWalls';
import type { Placement, WallDrawOptions, WallMode } from '../../../bygning/wallDraw';
import { OPENING_PRESETS, presetById, type OpeningKind, type OpeningOptions } from '../../../bygning/openingPlace';
import type { DimOrient } from '../../../bygning/cadMeasure';
import type { RoomMode, RoomOptions } from '../../../bygning/roomTool';
import type { SlabKind, SlabMode, SlabOptions } from '../../../bygning/slabEdit';
import { cmText, type StairLocation, type StairMode, type StairOptions } from '../../../bygning/stairs';
import { Button } from '../../Button';
import { Kbd } from '../../Kbd';
import { CadIcon, commandIcon } from './cadIcons';
import styles from './GeometriChrome.module.css';

/** A hover / focus tip: the name and its key, as the dock and the bar show them. */
function Tip({ command, below = false }: { command: GeometriCommand; below?: boolean }) {
  const d = commandDef(command);
  return (
    <span className={below ? `${styles.tip} ${styles.tipBelow}` : styles.tip} role="presentation">
      {d.label}
      {d.keys && <Kbd>{d.keys}</Kbd>}
      {!d.ready && <span className={styles.soon}>kommer snart</span>}
    </span>
  );
}

// ------------------------------------------------------------ tool dock --

export interface ToolDockProps {
  tool: GeometriTool;
  onTool: (tool: GeometriTool) => void;
}

/**
 * The tool dock, bottom centre (Spacio): Vælg | Væg, Dæk/gulv, Rum, Zone |
 * Dør, Vindue, Åbning, Trappe | Mål, Målsætning. Icons with a tip naming the
 * tool and its key; a tool whose slice has not landed is shown dimmed and
 * says "kommer snart" — the toolbox is visible from the start, so the
 * editor's reach is legible, but nothing pretends to work.
 */
export function ToolDock({ tool, onTool }: ToolDockProps) {
  return (
    <div className={styles.dock} role="toolbar" aria-label="Værktøjer">
      {TOOL_GROUPS.map((group, gi) => (
        <div key={gi} className={styles.group}>
          {gi > 0 && <span className={styles.sep} aria-hidden="true" />}
          {group.map((t) => {
            const command = `tool:${t}` as const;
            const d = commandDef(command);
            const on = t === tool;
            return (
              <button
                key={t}
                type="button"
                className={`${styles.tool}${on ? ` ${styles.toolOn}` : ''}${d.ready ? '' : ` ${styles.toolSoon}`}`}
                aria-pressed={on}
                aria-disabled={!d.ready || undefined}
                aria-label={commandTitle(command)}
                aria-keyshortcuts={d.keys || undefined}
                onClick={() => onTool(t)}
              >
                <CadIcon name={commandIcon(command)} className={styles.toolIcon} />
                <Tip command={command} />
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// --------------------------------------------------------- options strip --

const WALL_MODES: { value: WallMode; label: string; title: string }[] = [
  { value: 'line', label: 'Linje', title: 'Tegn en kæde af vægge, klik for hvert hjørne' },
  { value: 'rectangle', label: 'Rektangel', title: 'Fire vægge fra to modsatte hjørner' },
  { value: 'trace', label: 'Fra planspor', title: 'En væg på et scannet plans spor' },
];
const PLACEMENTS: { value: Placement; label: string; title: string }[] = [
  { value: 'center', label: 'Midte', title: 'Den tegnede linje er væggens midte' },
  { value: 'inside', label: 'Inde', title: 'Den tegnede linje er væggens indvendige flade' },
  { value: 'outside', label: 'Ude', title: 'Den tegnede linje er væggens udvendige flade' },
];

/** A length typed into the strip: set on Enter or leaving the field; empty = `empty` (when allowed). */
function LengthField({ label, value, placeholder, onChange, min }: { label: string; value: number | null; placeholder?: string; onChange: (v: number | null) => void; min: number }) {
  const [text, setText] = useState(value === null ? '' : lengthInput(value));
  useEffect(() => setText(value === null ? '' : lengthInput(value)), [value]);
  const commit = () => {
    if (text.trim() === '' && placeholder) return onChange(null);
    const v = parseLength(text);
    if (v !== null && v >= min) onChange(v);
    else setText(value === null ? '' : lengthInput(value));
  };
  return (
    <label className={styles.optField}>
      <span className={styles.optLabel}>{label}</span>
      <span className={styles.optInputWrap}>
        <input
          className={styles.optInput}
          value={text}
          placeholder={placeholder}
          inputMode="decimal"
          aria-label={label}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commit();
              e.currentTarget.blur();
            } else if (e.key === 'Escape') {
              e.stopPropagation();
              setText(value === null ? '' : lengthInput(value));
              e.currentTarget.blur();
            }
          }}
        />
        {text.trim() !== '' && <span className={styles.optUnit}>m</span>}
      </span>
    </label>
  );
}

function Segmented<T extends string>({ label, options, value, onChange }: { label: string; options: { value: T; label: string; title: string; soon?: boolean }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div role="radiogroup" aria-label={label} className={styles.segs}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          aria-disabled={o.soon || undefined}
          title={o.title}
          className={`${styles.seg} ${styles.segSm}${o.value === value ? ` ${styles.segOn}` : ''}${o.soon ? ` ${styles.segSoon}` : ''}`}
          onClick={() => !o.soon && onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export interface WallOptionsStripProps {
  options: WallDrawOptions;
  onChange: (patch: Partial<WallDrawOptions>) => void;
}

/**
 * The Væg tool's options strip, right above the dock while the tool is on
 * (Revit's Options Bar, Spacio's sub-modes; mock 02): Linje | Rektangel |
 * Fra planspor, Tykkelse, Placering (where the drawn line sits in the
 * wall), Højde (empty = to the storey's ceiling) and Kæde.
 */
export function WallOptionsStrip({ options, onChange }: WallOptionsStripProps) {
  return (
    <div className={styles.strip} role="toolbar" aria-label="Væg: indstillinger">
      <span className={styles.stripTitle}>Væg</span>
      <Segmented label="Tegnemåde" options={WALL_MODES} value={options.mode} onChange={(mode) => onChange({ mode })} />
      <span className={styles.vsep} aria-hidden="true" />
      <LengthField label="Tykkelse" value={options.thickness} min={0.01} onChange={(v) => v !== null && onChange({ thickness: v })} />
      <div className={styles.optField}>
        <span className={styles.optLabel}>Placering</span>
        <Segmented label="Placering" options={PLACEMENTS} value={options.placement} onChange={(placement) => onChange({ placement })} />
      </div>
      <LengthField label="Højde" value={options.height} placeholder="Til loft" min={0.1} onChange={(height) => onChange({ height })} />
      {options.mode === 'line' && (
        <label className={styles.optCheck} title="Hvert klik fortsætter fra det sidste punkt">
          <input type="checkbox" checked={options.chain} onChange={(e) => onChange({ chain: e.target.checked })} />
          Kæde
        </label>
      )}
    </div>
  );
}

const OPENING_TITLE: Record<OpeningKind, string> = { door: 'Dør', window: 'Vindue', opening: 'Åbning' };
const LEAF_OPTIONS: { value: '1' | '2'; label: string; title: string }[] = [
  { value: '1', label: 'Enkelt', title: 'Én fløj' },
  { value: '2', label: 'Dobbelt', title: 'To fløje, hængslet i hver side' },
];
const HAND_OPTIONS: { value: 'V' | 'H'; label: string; title: string }[] = [
  { value: 'V', label: 'V', title: 'Hængsel til venstre, set fra den side døren slår til (H skifter)' },
  { value: 'H', label: 'H', title: 'Hængsel til højre, set fra den side døren slår til (H skifter)' },
];

export interface OpeningOptionsStripProps {
  kind: OpeningKind;
  options: OpeningOptions;
  onChange: (patch: Partial<OpeningOptions>) => void;
}

/**
 * The Dør / Vindue / Åbning tools' options strip (mock 03): the type (a
 * preset fills the measures; typing one over leaves the type unnamed),
 * Bredde, Højde, Brystning; a door's leaves and hand (V / H, as seen from
 * the side it swings to); Centrér (in the free stretch of wall).
 */
export function OpeningOptionsStrip({ kind, options, onChange }: OpeningOptionsStripProps) {
  const presets = OPENING_PRESETS[kind];
  const preset = presetById(kind, options.presetId);
  const measure = (key: 'width' | 'height' | 'sill') => (v: number | null) => {
    if (v === null || v === options[key]) return;
    onChange({ [key]: v, presetId: null });
  };
  return (
    <div className={styles.strip} role="toolbar" aria-label={`${OPENING_TITLE[kind]}: indstillinger`}>
      <span className={styles.stripTitle}>{OPENING_TITLE[kind]}</span>
      <label className={styles.optSelectWrap} title="Type">
        <span className={styles.optSelectText}>{preset?.label ?? 'Egne mål'}</span>
        <CadIcon name="chevron" className={styles.optChevron} />
        <select
          className={styles.optSelect}
          aria-label="Type"
          value={options.presetId ?? ''}
          onChange={(e) => {
            const p = presetById(kind, e.target.value);
            if (p) onChange({ presetId: p.id, width: p.width, height: p.height, sill: p.sill, leaves: p.leaves });
          }}
        >
          {options.presetId === null && <option value="">Egne mål</option>}
          {presets.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </label>
      <span className={styles.vsep} aria-hidden="true" />
      <LengthField label="Bredde" value={options.width} min={0.3} onChange={measure('width')} />
      <LengthField label="Højde" value={options.height} min={0.1} onChange={measure('height')} />
      <LengthField label="Brystning" value={options.sill} min={0} onChange={measure('sill')} />
      {kind === 'door' && (
        <>
          <span className={styles.vsep} aria-hidden="true" />
          <div className={styles.optField}>
            <span className={styles.optLabel}>Fløje</span>
            <Segmented label="Fløje" options={LEAF_OPTIONS} value={String(options.leaves) as '1' | '2'} onChange={(v) => onChange({ leaves: v === '2' ? 2 : 1, presetId: null })} />
          </div>
          {options.leaves === 1 && (
            <div className={styles.optField}>
              <span className={styles.optLabel}>Hængsel</span>
              <Segmented label="Hængsel" options={HAND_OPTIONS} value={options.hand} onChange={(hand) => onChange({ hand })} />
            </div>
          )}
        </>
      )}
      <label className={styles.optCheck} title="Midt i det frie stykke væg mellem naboerne">
        <input type="checkbox" checked={options.centre} onChange={(e) => onChange({ centre: e.target.checked })} />
        Centrér
      </label>
    </div>
  );
}

const TAPE_MODES: { value: 'measure' | 'guide'; label: string; title: string }[] = [
  { value: 'measure', label: 'Mål', title: 'Andet klik måler afstanden (Ctrl: læg en hjælpelinje i stedet)' },
  { value: 'guide', label: 'Hjælpelinje', title: 'Andet klik lægger en hjælpelinje eller et hjælpepunkt (Ctrl: mål i stedet)' },
];
const DIM_ORIENTS: { value: DimOrient; label: string; title: string }[] = [
  { value: 'aligned', label: 'Justeret', title: 'Langs de to første punkter' },
  { value: 'x', label: 'Rød akse', title: 'Langs bygningens røde akse' },
  { value: 'y', label: 'Grøn akse', title: 'Langs bygningens grønne akse' },
];

/** What "lokalt" means, wherever the badge is shown. */
export const LOCAL_TITLE = 'Hjælpelinjer og målkæder gemmes kun i denne browser, til modellen selv kan gemme dem — de deles ikke med andre og slettes, når du logger ud';

/** The "Lokalt" badge: guides and chains are not on the server. */
export function LocalBadge() {
  return (
    <span className={styles.localBadge} title={LOCAL_TITLE}>
      Lokalt
    </span>
  );
}

export interface MeasureOptionsStripProps {
  tool: 'measure' | 'dimension';
  guides: boolean;
  orient: DimOrient;
  onChange: (patch: { guides?: boolean; orient?: DimOrient }) => void;
}

/**
 * Mål's and Målsætning's options strip (S9): Mål's second click measures or
 * leaves a guide (Ctrl flips it for one click, SketchUp); a chain measures
 * along its first two points or the storey's red / green axis. Both carry
 * the "Lokalt" badge: nothing of it reaches the server yet.
 */
export function MeasureOptionsStrip({ tool, guides, orient, onChange }: MeasureOptionsStripProps) {
  return (
    <div className={styles.strip} role="toolbar" aria-label={`${tool === 'measure' ? 'Mål' : 'Målsætning'}: indstillinger`}>
      <span className={styles.stripTitle}>{tool === 'measure' ? 'Mål' : 'Målsætning'}</span>
      {tool === 'measure' ? (
        <div className={styles.optField}>
          <span className={styles.optLabel}>Andet klik</span>
          <Segmented label="Andet klik" options={TAPE_MODES} value={guides ? 'guide' : 'measure'} onChange={(v) => onChange({ guides: v === 'guide' })} />
          <span className={styles.optLabel}>
            <Kbd>Ctrl</Kbd> skifter
          </span>
        </div>
      ) : (
        <div className={styles.optField}>
          <span className={styles.optLabel}>Retning</span>
          <Segmented label="Retning" options={DIM_ORIENTS} value={orient} onChange={(o) => onChange({ orient: o })} />
        </div>
      )}
      <span className={styles.vsep} aria-hidden="true" />
      <LocalBadge />
    </div>
  );
}

/** A free-text field in the strip: set on Enter or leaving it, Esc puts it back. */
function TextField({ label, value, placeholder, onChange, wide = false }: { label: string; value: string; placeholder?: string; onChange: (v: string) => void; wide?: boolean }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <label className={styles.optField}>
      <span className={styles.optLabel}>{label}</span>
      <span className={styles.optInputWrap}>
        <input
          className={`${styles.optInput} ${styles.optText}${wide ? ` ${styles.optTextWide}` : ''}`}
          value={text}
          placeholder={placeholder}
          aria-label={label}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => text !== value && onChange(text)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (text !== value) onChange(text);
              e.currentTarget.blur();
            } else if (e.key === 'Escape') {
              e.stopPropagation();
              setText(value);
              e.currentTarget.blur();
            }
          }}
        />
      </span>
    </label>
  );
}

const ROOM_MODES: { value: RoomMode; label: string; title: string }[] = [
  { value: 'place', label: 'Placér i lukket område', title: 'Klik inde mellem væggene: rummet følger dem' },
  { value: 'polygon', label: 'Tegn polygon', title: 'Klik rummets hjørner; klik det første (eller ↵) for at lukke' },
  { value: 'split', label: 'Del rum', title: 'To klik på tværs af et rum deler det langs linjen (SL med et rum valgt)' },
];
const ROOM_KINDS: { value: 'room' | 'corridor' | 'open_plan'; label: string; title: string }[] = [
  { value: 'room', label: 'Rum', title: 'Et almindeligt rum' },
  { value: 'corridor', label: 'Gang', title: 'Gang, entré eller trapperum' },
  { value: 'open_plan', label: 'Åben', title: 'Åbent rum (køkken-alrum, kontorlandskab)' },
];

export interface RoomOptionsStripProps {
  options: RoomOptions;
  /** The name a new room gets when Navn is left empty ("Rum 6"). */
  nextName: string;
  onChange: (patch: Partial<RoomOptions>) => void;
  /** Rum fra vægge: the storey's rooms rebuilt from its walls, previewed first. */
  onFromWalls: () => void;
}

/**
 * The Rum tool's options strip (mock 05): Placér i lukket område | Tegn
 * polygon | Del rum, Fra vægge (the whole storey, previewed in the banner),
 * and what a new room is called and what kind it is.
 */
export function RoomOptionsStrip({ options, nextName, onChange, onFromWalls }: RoomOptionsStripProps) {
  return (
    <div className={styles.strip} role="toolbar" aria-label="Rum: indstillinger">
      <span className={styles.stripTitle}>Rum</span>
      <Segmented label="Måde" options={ROOM_MODES} value={options.mode} onChange={(mode) => onChange({ mode })} />
      <button type="button" className={styles.linkButton} title="Genopbyg etagens rum fra væggene — vises før de gemmes" onClick={onFromWalls}>
        Fra vægge…
      </button>
      {options.mode !== 'split' && (
        <>
          <span className={styles.vsep} aria-hidden="true" />
          <TextField label="Navn" value={options.name} placeholder={nextName} onChange={(name) => onChange({ name })} />
          <Segmented label="Art" options={ROOM_KINDS} value={options.kind === 'unknown' ? 'room' : options.kind} onChange={(kind) => onChange({ kind })} />
        </>
      )}
    </div>
  );
}

const SLAB_MODES: { value: SlabMode; label: string; title: string }[] = [
  { value: 'polygon', label: 'Tegn polygon', title: 'Klik dækkets hjørner; klik det første (eller ↵) for at lukke' },
  { value: 'room', label: 'Fra rum', title: 'Klik i et rum: dækket følger rummets omrids' },
  { value: 'hole', label: 'Hul', title: 'Tegn et hul (trappehul, skakt) i det valgte dæk' },
];
const SLAB_KINDS: { value: SlabKind; label: string; title: string }[] = [
  { value: 'floor', label: 'Gulv', title: 'Etageadskillelse: overkanten i etagens gulv' },
  { value: 'ceiling', label: 'Loft', title: 'Loft: underkanten i etagens loft' },
  { value: 'roof', label: 'Tag', title: 'Tag: underkanten i etagens loft' },
];

export interface SlabOptionsStripProps {
  options: SlabOptions;
  onChange: (patch: Partial<SlabOptions>) => void;
}

/**
 * The Dæk / gulv tool's options strip (S7): Tegn polygon | Fra rum | Hul,
 * the kind (Gulv / Loft / Tag), Tykkelse and Overkant (over the storey
 * floor; empty = where the kind sits).
 */
export function SlabOptionsStrip({ options, onChange }: SlabOptionsStripProps) {
  return (
    <div className={styles.strip} role="toolbar" aria-label="Dæk: indstillinger">
      <span className={styles.stripTitle}>Dæk</span>
      <Segmented label="Måde" options={SLAB_MODES} value={options.mode} onChange={(mode) => onChange({ mode })} />
      {options.mode !== 'hole' && (
        <>
          <span className={styles.vsep} aria-hidden="true" />
          <Segmented label="Art" options={SLAB_KINDS} value={options.kind} onChange={(kind) => onChange({ kind })} />
          <LengthField label="Tykkelse" value={options.thickness} min={0.01} onChange={(v) => v !== null && onChange({ thickness: v })} />
          <LengthField label="Overkant" value={options.level} placeholder="Auto" min={-50} onChange={(level) => onChange({ level })} />
        </>
      )}
    </div>
  );
}

export interface ZoneOptionsStripProps {
  zones: { guid: string; name: string; swatch: string }[];
  /** The zone the clicks edit; null = gathering a new one. */
  target: string | null;
  picks: number;
  name: string;
  use: string;
  nextName: string;
  onTarget: (guid: string | null) => void;
  onChange: (patch: { zoneName?: string; zoneUse?: string }) => void;
  onCreate: () => void;
}

/**
 * The Zone tool's options strip: which zone the clicks edit (or a new
 * one: its name and use, and Opret zone with the rooms clicked so far).
 */
export function ZoneOptionsStrip({ zones, target, picks, name, use, nextName, onTarget, onChange, onCreate }: ZoneOptionsStripProps) {
  const current = zones.find((z) => z.guid === target) ?? null;
  return (
    <div className={styles.strip} role="toolbar" aria-label="Zone: indstillinger">
      <span className={styles.stripTitle}>Zone</span>
      <label className={styles.optSelectWrap} title="Zonen klikkene retter">
        {current && <i className={styles.swatch} style={{ background: `var(${current.swatch})` }} aria-hidden="true" />}
        <span className={styles.optSelectText}>{current ? current.name : 'Ny zone'}</span>
        <CadIcon name="chevron" className={styles.optChevron} />
        <select className={styles.optSelect} aria-label="Zone" value={current?.guid ?? ''} onChange={(e) => onTarget(e.target.value || null)}>
          <option value="">Ny zone</option>
          {zones.map((z) => (
            <option key={z.guid} value={z.guid}>
              {z.name}
            </option>
          ))}
        </select>
      </label>
      {current ? (
        <span className={styles.optLabel}>Klik rum for at tage dem med eller ud</span>
      ) : (
        <>
          <TextField label="Navn" value={name} placeholder={nextName} onChange={(zoneName) => onChange({ zoneName })} />
          <TextField label="Anvendelse" value={use} placeholder="fx bolig" onChange={(zoneUse) => onChange({ zoneUse })} />
          <Button size="sm" variant="primary" disabled={picks === 0} onClick={onCreate} title="Opret zonen af de klikkede rum (↵)">
            Opret zone{picks > 0 ? ` · ${picks} rum` : ''}
          </Button>
        </>
      )}
    </div>
  );
}

const STAIR_MODES: { value: StairMode; label: string; title: string; soon?: boolean }[] = [
  { value: 'straight', label: 'Lige løb', title: 'Ét løb; et klik før alle trin er sat lægger en repos' },
  { value: 'L', label: 'L-løb', title: 'To løb med en kvadratisk repos imellem, drejet mod musen' },
  { value: 'U', label: 'U-løb', title: 'To løb side om side, vendt på en repos' },
  { value: 'spiral', label: 'Spindel', title: 'Spindeltrappe — kommer snart', soon: true },
];
const STAIR_LOCATIONS: { value: StairLocation; label: string; title: string }[] = [
  { value: 'left', label: 'Venstre', title: 'Den tegnede linje er trappens venstre side, set gående op' },
  { value: 'centre', label: 'Midte', title: 'Den tegnede linje er trappens midte (ganglinjen)' },
  { value: 'right', label: 'Højre', title: 'Den tegnede linje er trappens højre side, set gående op' },
];

export interface StairOptionsStripProps {
  options: StairOptions;
  /** The storey the stair starts on ("Stueplan"). */
  from: string | null;
  /** The storeys it may arrive at (above the one it starts on), lowest first. */
  targets: { id: number; name: string }[];
  /** The storey it arrives at as things stand (the one above unless chosen). */
  to: number | null;
  /** The live count from the storey height: risers and the riser height. */
  risers: number | null;
  riser: number | null;
  onChange: (patch: Partial<StairOptions>) => void;
}

/**
 * The Trappe tool's options strip (mock 06): Lige løb | L-løb | U-løb,
 * Fra the shown storey til the one above (or a chosen one, or none),
 * Bredde, Placering of the drawn walking line, the going, whether to cut
 * the hole in the floor it arrives at — and the live count the storey
 * height gives ("16 trin · 18,1 cm").
 */
export function StairOptionsStrip({ options, from, targets, to, risers, riser, onChange }: StairOptionsStripProps) {
  return (
    <div className={styles.strip} role="toolbar" aria-label="Trappe: indstillinger">
      <span className={styles.stripTitle}>Trappe</span>
      <Segmented label="Løb" options={STAIR_MODES} value={options.mode} onChange={(mode) => onChange({ mode })} />
      <span className={styles.vsep} aria-hidden="true" />
      <span className={styles.optField}>
        <span className={styles.optLabel}>Fra</span>
        <span className={styles.optValue}>{from ?? '—'}</span>
      </span>
      <label className={styles.optSelectWrap} title="Etagen trappen ankommer til">
        <span className={styles.optLabel}>til</span>
        <span className={styles.optSelectText}>{to === null ? 'Ingen' : (targets.find((t) => t.id === to)?.name ?? '—')}</span>
        <CadIcon name="chevron" className={styles.optChevron} />
        <select
          className={styles.optSelect}
          aria-label="Til etage"
          value={to === null ? 'none' : String(to)}
          onChange={(e) => onChange({ toStorey: e.target.value === 'none' ? null : Number(e.target.value) })}
        >
          {targets.map((t) => (
            <option key={t.id} value={String(t.id)}>
              {t.name}
            </option>
          ))}
          <option value="none">Ingen (trin i etagen)</option>
        </select>
      </label>
      <LengthField label="Bredde" value={options.width} min={0.3} onChange={(v) => v !== null && onChange({ width: v })} />
      <div className={styles.optField}>
        <span className={styles.optLabel}>Placering</span>
        <Segmented label="Placering" options={STAIR_LOCATIONS} value={options.location} onChange={(location) => onChange({ location })} />
      </div>
      <LengthField label="Grund" value={options.going} min={0.1} onChange={(v) => v !== null && onChange({ going: v })} />
      <label className={styles.optCheck} title={to === null ? 'Trappen ankommer ikke til en etage' : 'Skær trappens fodaftryk ud af dækket den ankommer til'}>
        <input type="checkbox" checked={options.cutSlab && to !== null} disabled={to === null} onChange={(e) => onChange({ cutSlab: e.target.checked })} />
        Hul i dæk
      </label>
      {risers !== null && riser !== null && (
        <>
          <span className={styles.vsep} aria-hidden="true" />
          <span className={styles.optField} title="Fra etagehøjden: nok trin til højst 18 cm stigning">
            <span className={styles.optValue}>{risers} trin</span>
            <span className={styles.optLabel}>· {cmText(riser)}</span>
          </span>
        </>
      )}
    </div>
  );
}

export interface RoomPreviewBannerProps {
  title: string;
  summary: string;
  busy: boolean;
  onApply: () => void;
  onCancel: () => void;
}

/** A room tool's preview waiting for Anvend, top centre (mock 05): what it will do, Annullér, Anvend. */
export function RoomPreviewBanner({ title, summary, busy, onApply, onCancel }: RoomPreviewBannerProps) {
  const canEdit = useCanEdit();
  return (
    <div className={styles.banner} role="status" aria-live="polite">
      <span className={styles.bannerText}>
        <b>{title}</b> · forhåndsvisning: {summary}
      </span>
      <Button size="sm" variant="filled" onClick={onCancel} title="Esc">
        Annullér
      </Button>
      <Button size="sm" variant="primary" onClick={onApply} disabled={busy || !canEdit} title="↵">
        {busy ? 'Anvender…' : 'Anvend'}
      </Button>
    </div>
  );
}

export interface ModifyStripProps {
  session: ModifySession;
  /** "Væg 4": what the command works against (the boundary, the wall to align). */
  subject: string | null;
  onCopy: (copy: boolean) => void;
  onDone: () => void;
}

/** A running modify command's strip: its name, what it works on, Kopi (Rotér / Spejl), Afslut. */
export function ModifyStrip({ session, subject, onCopy, onDone }: ModifyStripProps) {
  const c = session.command;
  const role = c === 'trim' ? 'Grænse' : c === 'align' ? 'Justerer' : c === 'join' ? 'Fra' : c === 'offset' ? 'Kopi af' : null;
  return (
    <div className={styles.strip} role="toolbar" aria-label={`${COMMAND_LABEL[c]}: indstillinger`}>
      <span className={styles.stripTitle}>{COMMAND_LABEL[c]}</span>
      {role && subject && (
        <span className={styles.optField}>
          <span className={styles.optLabel}>{role}</span>
          <b className={styles.optValue}>{subject}</b>
        </span>
      )}
      {(c === 'move' || c === 'copy' || c === 'rotate' || c === 'mirror') && (
        <span className={styles.optField}>
          <span className={styles.optLabel}>Valgt</span>
          <b className={styles.optValue}>{session.guids.length === 1 ? '1 element' : `${session.guids.length} elementer`}</b>
        </span>
      )}
      {(c === 'rotate' || c === 'mirror') && (
        <label className={styles.optCheck} title="Behold originalen og læg en kopi">
          <input type="checkbox" checked={session.copy} onChange={(e) => onCopy(e.target.checked)} />
          Kopi
        </label>
      )}
      <button type="button" className={styles.linkButton} onClick={onDone}>
        Afslut <Kbd>Esc</Kbd>
      </button>
    </div>
  );
}

// -------------------------------------------------------- selection bar --

export interface SelectionBarProps {
  /** "Væg 6", or "3 elementer". */
  title: string;
  count: number;
  /** Tilbage til detekteret applies to something selected. */
  revertable: boolean;
  /** Slet applies to something selected. */
  deletable: boolean;
  /**
   * Only guides and dimension chains are selected (S9): the modify
   * commands do not apply to them, and Slet is anyone's — they live in
   * this browser ("Lokalt").
   */
  annotationsOnly?: boolean;
  /**
   * The modify commands that apply to the selection (`applicableCommands`):
   * only these are shown — a stair gets Vend, not the wall tools. Absent:
   * every modify command.
   */
  commands?: ReadonlySet<GeometriCommand>;
  onCommand: (command: GeometriCommand) => void;
}

/**
 * The selection bar, top centre, dark (Spacio's contextual bar), only while
 * something is selected: what is selected, then Flyt, Kopiér, Rotér,
 * Spejl, Forskyd | Del, Saml hjørne, Forlæng/trim | Tilbage til
 * detekteret, Slet. The modify commands come with S4 and say so.
 */
export function SelectionBar({ title, count, revertable, deletable, annotationsOnly = false, commands, onCommand }: SelectionBarProps) {
  const canEdit = useCanEdit();
  const button = (command: GeometriCommand, enabled: boolean) => {
    const d = commandDef(command);
    // Deleting a guide or chain writes nothing to the model: a viewer may.
    const ok = enabled && d.ready && (canEdit || (annotationsOnly && command === 'delete'));
    return (
      <button
        key={command}
        type="button"
        className={`${styles.barButton}${ok ? '' : ` ${styles.barSoon}`}${command === 'delete' ? ` ${styles.barDanger}` : ''}`}
        aria-disabled={!ok || undefined}
        aria-label={d.ready ? (d.keys ? `${d.label} (${d.keys})` : d.label) : commandTitle(command)}
        aria-keyshortcuts={d.keys || undefined}
        onClick={() => onCommand(command)}
      >
        <CadIcon name={commandIcon(command)} className={styles.barIcon} />
        <Tip command={command} below />
      </button>
    );
  };
  return (
    <div className={styles.bar} role="toolbar" aria-label={`Valgt: ${title}`}>
      <span className={styles.barTitle}>
        {title}
        <small>{count === 1 ? '1 valgt' : `${count} valgt`}</small>
      </span>
      {annotationsOnly ? (
        <span className={styles.barGroup}>
          <span className={styles.barSep} aria-hidden="true" />
          <LocalBadge />
          {button('delete', deletable)}
        </span>
      ) : (
        <>
          {MODIFY_GROUPS.map((group) => group.filter((c) => !commands || commands.has(c)))
            .filter((group) => group.length > 0)
            .map((group, gi) => (
              <span key={gi} className={styles.barGroup}>
                <span className={styles.barSep} aria-hidden="true" />
                {group.map((c) => button(c, true))}
              </span>
            ))}
          {commands?.has('flip') && (
            <span className={styles.barGroup}>
              <span className={styles.barSep} aria-hidden="true" />
              {button('flip', true)}
            </span>
          )}
          <span className={styles.barGroup}>
            <span className={styles.barSep} aria-hidden="true" />
            {button('revert', revertable)}
            {button('delete', deletable)}
          </span>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------- view cluster --

export type GeometriView = 'plan' | '3d' | 'snit' | 'split';

export interface ViewClusterProps {
  view: GeometriView;
  onView: (view: GeometriView) => void;
  /** The scan cloud drawn under the model. */
  underlay: boolean;
  onUnderlay: () => void;
  onFit: () => void;
}

/** The view cluster, top right: Plan | 3D | Snit | Plan + snit, the scan underlay, zoom to fit. */
export function ViewCluster({ view, onView, underlay, onUnderlay, onFit }: ViewClusterProps) {
  const seg = (value: GeometriView, label: string, title?: string) => (
    <button
      key={value}
      type="button"
      role="radio"
      aria-checked={value === view}
      className={`${styles.seg}${value === view ? ` ${styles.segOn}` : ''}`}
      title={title}
      onClick={() => onView(value)}
    >
      {label}
    </button>
  );
  return (
    <div className={styles.views}>
      <div role="radiogroup" aria-label="Visning" className={styles.segs}>
        {seg('plan', 'Plan')}
        {seg('3d', '3D')}
        {seg('snit', 'Snit', 'Et lodret snit langs snitlinjen A–A (SN gennem det valgte)')}
        {seg('split', 'Plan + snit', 'Planen over snittet: tegn snitlinjen, træk etagehøjderne')}
      </div>
      <span className={styles.vsep} aria-hidden="true" />
      <button
        type="button"
        className={`${styles.iconButton}${underlay ? ` ${styles.iconOn}` : ''}`}
        aria-pressed={underlay}
        title={underlay ? 'Skjul scanningen under modellen' : 'Vis scanningen under modellen'}
        aria-label="Scanning som underlag"
        onClick={onUnderlay}
      >
        <CadIcon name="cloud" className={styles.icon} />
      </button>
      <button type="button" className={styles.iconButton} title="Zoom til alt" aria-label="Zoom til alt" onClick={onFit}>
        <CadIcon name="fit" className={styles.icon} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------- storey chip --

export interface StoreyChipProps {
  storeys: readonly { id: number; name: string; z: number }[];
  value: number | null;
  onChange: (id: number | null) => void;
}

const levelText = (z: number) => (Math.abs(z) < 0.005 ? '±0,00' : `${z > 0 ? '+' : '−'}${Math.abs(z).toFixed(2).replace('.', ',')}`);

/** The storey chip in the sub-tab bar ("Stueplan ±0,00 ▾"): which storey the plan cuts and the editor works on. */
export function StoreyChip({ storeys, value, onChange }: StoreyChipProps) {
  const current = storeys.find((s) => s.id === value) ?? null;
  return (
    <label className={styles.storey}>
      <span className={styles.storeyName}>{current ? current.name : 'Alle etager'}</span>
      {current && <small className={styles.storeyZ}>{levelText(current.z)}</small>}
      <CadIcon name="chevron" className={styles.storeyChevron} />
      <select
        className={styles.storeySelect}
        aria-label="Etage"
        value={value === null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      >
        <option value="">Alle etager</option>
        {storeys.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name} {levelText(s.z)}
          </option>
        ))}
      </select>
    </label>
  );
}

// ---------------------------------------------------------- status line --

export interface StatusLineProps {
  tool: GeometriTool;
  selected: number;
  /** The Væg tool's sub-mode, for its hint. */
  wallMode?: 'line' | 'rectangle' | 'trace';
  roomMode?: 'place' | 'polygon' | 'split';
  slabMode?: SlabMode;
  /** A tool's own hint (S3: a drag's); else the tool's. */
  hint?: { lead: string; keys: { key: string; text: string }[] } | null;
  toggles: { key: string; label: string; on: boolean; title: string }[];
  onToggle: (key: string) => void;
  cursor: XY | null;
  /** A drag's readout replaces the cursor's ("Δx 5,70 Δy 0,00"). */
  readout?: string | null;
  warnings: readonly GeoWarning[];
  onPickWarning: (w: GeoWarning) => void;
  /** "Gemt · rev 142", "Gemmer…". */
  saved: string;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  /** The solids lag the model ("Dæk & tag forældet"). */
  stale: boolean;
  rederiving: boolean;
  onRederive: () => void;
  /** Guides and dimension chains on the shown storey (S9): the list and its clean-up. */
  annotations?: AnnotationsStatus | null;
}

/** One guide or chain in the status line's list. */
export interface AnnotationItem {
  id: string;
  label: string;
}

export interface AnnotationsStatus {
  guides: AnnotationItem[];
  dims: AnnotationItem[];
  /** Shown with none too (Mål or Målsætning is the tool). */
  always: boolean;
  onRemove: (id: string) => void;
  onClear: (what: 'guides' | 'dims') => void;
}

const coord = (v: number) => v.toFixed(2).replace('.', ',').replace('-', '−');

/**
 * The status line, along the bottom (SketchUp's): the tool hint with its
 * keys, the snap toggles, the cursor (or a drag's readout), the warning
 * count (its popover replaces S1's always-on Advarsler panel), whether the
 * model is saved and at which revision, the solids' state, undo / redo.
 */
export function StatusLine(props: StatusLineProps) {
  const { tool, selected, toggles, onToggle, cursor, readout, warnings, onPickWarning, saved, canUndo, canRedo, onUndo, onRedo, stale, rederiving, onRederive } = props;
  const canEdit = useCanEdit();
  const hint = props.hint ?? toolHint(tool, selected, props.wallMode, props.roomMode, props.slabMode);
  const [open, setOpen] = useState(false);
  const popId = useId();
  const popRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (popRef.current && e.target instanceof Node && !popRef.current.contains(e.target)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener('pointerdown', close, true);
    window.addEventListener('keydown', esc, true);
    return () => {
      window.removeEventListener('pointerdown', close, true);
      window.removeEventListener('keydown', esc, true);
    };
  }, [open]);

  let solids: ReactNode = null;
  if (rederiving) solids = <span className={styles.statusNote}>Dæk & tag genberegnes…</span>;
  else if (stale)
    solids = (
      <button type="button" className={styles.linkButton} onClick={onRederive} disabled={!canEdit} title="Dæk og tag følger ikke de seneste ændringer — genberegn dem">
        Dæk & tag forældet · Genberegn
      </button>
    );

  return (
    <div className={styles.status} data-shell-footer="">
      <p className={styles.hint}>
        <b>{hint.lead}</b>
        {hint.keys.map((k) => (
          <span key={k.key} className={styles.hintKey}>
            <Kbd>{k.key}</Kbd>
            {k.text}
          </span>
        ))}
      </p>
      <div className={styles.right}>
        <div className={styles.toggles} role="group" aria-label="Fang">
          {toggles.map((t) => (
            <button key={t.key} type="button" className={`${styles.toggle}${t.on ? ` ${styles.toggleOn}` : ''}`} aria-pressed={t.on} title={t.title} onClick={() => onToggle(t.key)}>
              <i aria-hidden="true" />
              {t.label}
            </button>
          ))}
        </div>
        <span className={styles.readout} aria-live="off" data-testid="geo-readout">
          {readout ?? (cursor ? `x ${coord(cursor[0])}  y ${coord(cursor[1])}` : '')}
        </span>
        {props.annotations && (props.annotations.always || props.annotations.guides.length + props.annotations.dims.length > 0) && (
          <AnnotationsButton status={props.annotations} />
        )}
        {solids}
        <div className={styles.warnWrap} ref={popRef}>
          <button
            type="button"
            className={warnings.length > 0 ? `${styles.warnPill} ${styles.warnPillOn}` : styles.warnPill}
            aria-expanded={open}
            aria-controls={popId}
            title="Advarsler"
            onClick={() => setOpen((o) => !o)}
          >
            <CadIcon name="warning" className={styles.warnIcon} />
            {warnings.length}
          </button>
          {open && (
            <div id={popId} className={styles.popover} role="dialog" aria-label="Advarsler">
              <h2 className={styles.popTitle}>Advarsler ({warnings.length})</h2>
              {warnings.length === 0 ? (
                <p className={styles.popNone}>Ingen advarsler på det viste.</p>
              ) : (
                <ul className={styles.popList}>
                  {warnings.map((w) => (
                    <li key={w.id}>
                      <button
                        type="button"
                        className={styles.popItem}
                        onClick={() => {
                          onPickWarning(w);
                          setOpen(false);
                        }}
                      >
                        {w.text}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <span className={styles.saved}>{saved}</span>
        <div className={styles.undo}>
          <button type="button" className={styles.iconButtonSm} aria-label="Fortryd (⌘Z)" title="Fortryd (⌘Z)" disabled={!canUndo} onClick={onUndo}>
            <CadIcon name="undo" className={styles.iconSm} />
          </button>
          <button type="button" className={styles.iconButtonSm} aria-label="Gentag (⇧⌘Z)" title="Gentag (⇧⌘Z)" disabled={!canRedo} onClick={onRedo}>
            <CadIcon name="redo" className={styles.iconSm} />
          </button>
        </div>
      </div>
    </div>
  );
}

/** A popover's open state: closes on a click outside it or Esc. */
function usePopover(): { open: boolean; setOpen: (o: boolean | ((o: boolean) => boolean)) => void; ref: RefObject<HTMLDivElement | null> } {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (ref.current && e.target instanceof Node && !ref.current.contains(e.target)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener('pointerdown', close, true);
    window.addEventListener('keydown', esc, true);
    return () => {
      window.removeEventListener('pointerdown', close, true);
      window.removeEventListener('keydown', esc, true);
    };
  }, [open]);
  return { open, setOpen, ref };
}

const countText = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The status line's guides and chains (S9): "Hjælpelinjer 3 · Mål 1"
 * opens their list — remove one, or clear a kind — under the "Lokalt"
 * badge (they live in this browser only).
 */
function AnnotationsButton({ status }: { status: AnnotationsStatus }) {
  const { open, setOpen, ref } = usePopover();
  const popId = useId();
  const { guides, dims } = status;
  const section = (title: string, items: AnnotationItem[], what: 'guides' | 'dims', none: string) => (
    <section className={styles.annSection}>
      <div className={styles.annHead}>
        <h2 className={styles.popTitle}>
          {title} ({items.length})
        </h2>
        {items.length > 0 && (
          <button type="button" className={styles.linkButton} onClick={() => status.onClear(what)}>
            Ryd
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <p className={styles.popNone}>{none}</p>
      ) : (
        <ul className={styles.popList}>
          {items.map((it) => (
            <li key={it.id} className={styles.annRow}>
              <span>{it.label}</span>
              <button type="button" className={styles.iconButtonSm} aria-label={`Fjern ${it.label}`} title={`Fjern ${it.label}`} onClick={() => status.onRemove(it.id)}>
                <CadIcon name="delete" className={styles.iconSm} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
  return (
    <div className={styles.warnWrap} ref={ref}>
      <button type="button" className={styles.warnPill} aria-expanded={open} aria-controls={popId} title="Hjælpelinjer og målkæder" onClick={() => setOpen((o) => !o)}>
        <CadIcon name="measure" className={styles.warnIcon} />
        {countText(guides.length, 'hjælpelinje', 'hjælpelinjer')} · {countText(dims.length, 'målkæde', 'målkæder')}
      </button>
      {open && (
        <div id={popId} className={styles.popover} role="dialog" aria-label="Hjælpelinjer og målkæder">
          <p className={styles.annLocal}>
            <LocalBadge />
            <span>Kun i denne browser, til modellen kan gemme dem.</span>
          </p>
          {section('Hjælpelinjer', guides, 'guides', 'Ingen. Mål (M) med Ctrl lægger en.')}
          {section('Målkæder', dims, 'dims', 'Ingen. Målsætning (⇧M) tegner en.')}
        </div>
      )}
    </div>
  );
}
